import React, { useCallback, useMemo } from 'react';
import { useField } from 'formik';
import { useTranslation } from '@blocs.i18n';
import { BasicContainer, FlexDynamic, FlexStyleProps } from '../../atoms';
import { FieldWrapper, FieldWrapperProps } from './FieldWrapper';
import { CheckboxInput, CheckboxInputProps } from './CheckboxInput';
import { Option, validateRadioCheckOptionsProp } from './utils';

const SELECT_ALL_VALUE = 'selectAll';

const handleStringValuesOnChange = ({
  newVal,
  onChangeProp,
  areAllChecked,
  options,
  value,
}: {
  newVal: string;
  onChangeProp: (newValues: string[]) => void;
  areAllChecked: boolean;
  options: Option[];
  value: string[];
}) => {
  const optsWithoutSelectAll = options.filter((o) => o.value !== SELECT_ALL_VALUE);
  if (newVal === SELECT_ALL_VALUE && areAllChecked) {
    onChangeProp([]);
    return;
  }

  if (newVal === SELECT_ALL_VALUE) {
    onChangeProp(optsWithoutSelectAll.map((o) => o.value));
    return;
  }

  if (value.includes(newVal)) {
    onChangeProp(value.filter((v) => v !== newVal));
  } else {
    onChangeProp([...value, newVal]);
  }
};

const handleObjectValuesOnChange = ({
  newVal,
  onChangeProp,
  areAllChecked,
  options,
  value,
}: {
  newVal: string;
  onChangeProp: (newValues: Option[]) => void;
  areAllChecked: boolean;
  options: Option[];
  value: Option[];
}) => {
  const optsWithoutSelectAll = options.filter((o) => o.value !== SELECT_ALL_VALUE);
  if (newVal === SELECT_ALL_VALUE && areAllChecked) {
    onChangeProp([]);
    return;
  }

  if (newVal === SELECT_ALL_VALUE) {
    onChangeProp(optsWithoutSelectAll);
    return;
  }

  if (value.some((val) => val.value === newVal)) {
    onChangeProp(value.filter((v) => v.value !== newVal));
    return;
  }

  const foundOption = options.find((o) => o.value === newVal);
  if (foundOption) {
    onChangeProp([...value, foundOption]);
  }
};

const isOptionChecked = <ObjectValues extends boolean>({
  option,
  value,
  useObjectValues,
  areAllChecked,
}: {
  option: Option;
  value: ObjectValues extends true ? Option[] : string[];
  useObjectValues: ObjectValues;
  areAllChecked: boolean;
}) => {
  if (option.value === SELECT_ALL_VALUE && areAllChecked) {
    return true;
  }

  if (useObjectValues) {
    const optionValues = value as Option[];
    return optionValues.some((val) => val.value === option.value);
  }

  const stringValues = value as string[];
  return stringValues.includes(option.value);
};

export type UnconnectedCheckboxGroupProps<ObjectValues extends boolean> = {
  /** controls whether the values being managed are Option objects or strings, defaults to false */
  useObjectValues?: ObjectValues;
  /** the list of values currently selected in the group
   * this should be a list of strings matching the "value" property of the options if useObjectValues is falsy
   * or a list of Option objects if useObjectValues is true
   */
  value: ObjectValues extends true ? Option[] : string[];
  /** callback invoked when an option is selected or deselected
   * this will be passed a list of strings matching the "value" property of the options if useObjectValues is falsy
   * or a list of Option objects if useObjectValues is true
   */
  onChange: ObjectValues extends true ? (newValues: Option[]) => void : (newValues: string[]) => void;
  /** the list of checkbox options in the group */
  options: Option[];
  /** disable all checkboxes from changing */
  disabled?: boolean;
  /** size for the Checkbox elements */
  size?: CheckboxInputProps['size'];
  /** name for the form field in the form values */
  name: string;
  /** whether to include a select all option */
  withSelectAll?: boolean;
  /** custom label to use for the select all option, ignored if withSelectAll is falsy */
  selectAllLabel?: string;
  /** variant for the Checkbox elements */
  variant?: CheckboxInputProps['variant'];
  /** border radius for the Checkbox elements */
  borderRadius?: CheckboxInputProps['borderRadius'];
  /** gap spacing value between checkboxes (defaults to 2) */
  gap?: FlexStyleProps['gap'];
  /** alignment of the checkboxes */
  alignment?: 'horizontal' | 'vertical';
};

/**
 * A group of checkbox inputs of which all are tied to the same field in a form
 * (an array of string values)
 * you need to provide the value and onChange handlers to manage the state of the group
 * do not add state to this component
 * @name UnconnectedCheckboxGroup
 * @description use this if you need to compose complex inter-related form elements that include a checkbox group (or in inputs that are not connected to Formik).
 *
 * @example
 * const [selectedValue, setSelectedValue] = useState<string[]>([]); // state management in parent component
 * <UnconnectedCheckboxGroup
 *  name="input"
 *  label={t('common:my.label.key')}
 *  options={[{ value: '1', label: 'option 1' }, { value: '2', label: 'option 2' }]}
 *  value={selectedValue}
 *  onChange={setSelectedValue}
 * />
 */
export const UnconnectedCheckboxGroup = <ObjectValues extends boolean>({
  onChange: onChangeProp,
  options: optionsProp,
  value,
  disabled = false,
  size = 'lg',
  name,
  withSelectAll = false,
  selectAllLabel,
  variant = 'default',
  borderRadius = 'none',
  gap = '2',
  alignment = 'vertical',
  useObjectValues,
}: UnconnectedCheckboxGroupProps<ObjectValues>) => {
  const { t } = useTranslation();
  const options = useMemo(() => {
    let opts = Array.isArray(optionsProp) ? optionsProp : [];

    if (withSelectAll && opts.length > 0) {
      opts = [{ value: SELECT_ALL_VALUE, label: selectAllLabel || t('common:form.label.selectAll') }, ...opts];
    }

    return validateRadioCheckOptionsProp({ options: opts, componentName: 'UnconnectedCheckboxGroup' });
  }, [withSelectAll, optionsProp, selectAllLabel, t]);

  const areAllChecked = useMemo(() => {
    if (useObjectValues) {
      const optionValues = value as Option[];
      const uncheckedOpts = options.filter(
        (o) => o.value !== SELECT_ALL_VALUE && !optionValues.some((val) => val.value === o.value)
      );
      return uncheckedOpts.length === 0;
    }

    const stringValues = value as string[];
    const uncheckedOpts = options.filter((o) => {
      return o.value !== SELECT_ALL_VALUE && !stringValues.includes(o.value);
    });
    return uncheckedOpts.length === 0;
  }, [options, value, useObjectValues]);

  const onChange = useCallback(
    (newVal: string) => {
      if (useObjectValues) {
        handleObjectValuesOnChange({
          newVal,
          areAllChecked,
          options,
          onChangeProp: onChangeProp as (newValues: Option[]) => void,
          value: value as Option[],
        });
      } else {
        handleStringValuesOnChange({
          newVal,
          areAllChecked,
          options,
          onChangeProp: onChangeProp as (newValues: string[]) => void,
          value: value as string[],
        });
      }
    },
    [areAllChecked, onChangeProp, options, value, useObjectValues]
  );

  return (
    <FlexDynamic
      data-testid={`checkbox-group-${name}`}
      direction={alignment === 'horizontal' ? 'row' : 'column'}
      gap={gap}
      mt="2"
      w="full"
    >
      {options.map((option) => (
        <React.Fragment key={option.value}>
          <CheckboxInput
            value={option.value}
            label={option.label}
            checked={isOptionChecked({ option, value, useObjectValues, areAllChecked })}
            onChange={(newValue) => onChange(newValue)}
            disabled={disabled}
            size={size}
            name={name}
            variant={variant}
            borderRadius={borderRadius}
          />
          {option.value === SELECT_ALL_VALUE && (
            <BasicContainer w="full" data-testid="select-all-checkbox-divider" borderPlacement="bottom" />
          )}
        </React.Fragment>
      ))}
    </FlexDynamic>
  );
};

export interface WrappedUnconnectedCheckboxGroupProps<ObjectValues extends boolean> {
  inputProps: UnconnectedCheckboxGroupProps<ObjectValues>;
  wrapperProps?: FieldWrapperProps;
}

export const WrappedUnconnectedCheckboxGroup = <ObjectValues extends boolean>({
  wrapperProps,
  inputProps,
}: WrappedUnconnectedCheckboxGroupProps<ObjectValues>) => {
  return (
    <FieldWrapper {...wrapperProps}>
      <UnconnectedCheckboxGroup {...inputProps} />
    </FieldWrapper>
  );
};

type ControlledCheckboxGroupProps<ObjectValues extends boolean> = Omit<
  UnconnectedCheckboxGroupProps<ObjectValues>,
  'onChange' | 'value'
>;

export interface CheckboxGroupInputProps<ObjectValues extends boolean> {
  /** full props for the underlying UnconnectedCheckboxGroup component */
  inputProps?: Omit<ControlledCheckboxGroupProps<ObjectValues>, 'options'>;
  /** full props for the underlying FieldWrapper component */
  wrapperProps?: FieldWrapperProps;
  /** array of configuration for the Checkbox options in the group */
  options: ControlledCheckboxGroupProps<ObjectValues>['options'];
  /** name of the form field in the Formik form values */
  name: string;
  /** disable the Checkbox inputs in the group */
  disabled?: boolean;
  /** label for the form field Checkboxgroup */
  label: FieldWrapperProps['label'];
  /** help text for the form field */
  helpText?: FieldWrapperProps['helpText'];
  /** size for the Checkbox elements */
  size?: CheckboxInputProps['size'];
  /** include a select all option */
  withSelectAll?: boolean;
  /** custom label to use for the select all option, ignored if withSelectAll is falsy */
  selectAllLabel?: string;
  /** alignment for the checkbox group */
  alignment?: 'horizontal' | 'vertical';
  /** controls whether the values being managed are Option objects or strings, defaults to false */
  useObjectValues?: ObjectValues;
}

/**
 * A group of checkbox inputs of which all are tied to the same field in a form
 * (an array of string values)
 * @name CheckboxGroupInput
 * @description this should be used as the normal component for groups of checkbox inputs for all Formik forms.
 * it will automatically render any validation errors and will correctly set a11y roles for the elements
 *
 * @example
 * <CheckboxGroupInput
 *  name="input"
 *  label={t('common:my.label.key')}
 *  options={[{ value: '1', label: 'option 1' }, { value: '2', label: 'option 2' }]}
 * />
 *
 * @example with selectAll enabled
 * <CheckboxGroupInput
 *  name="input"
 *  label={t('common:my.label.key')}
 *  options={[{ value: '1', label: 'option 1' }, { value: '2', label: 'option 2' }]}
 *  withSelectAll
 * />
 */
export const CheckboxGroupInput = <ObjectValues extends boolean>({
  disabled,
  helpText,
  inputProps,
  label,
  name,
  options,
  wrapperProps,
  size = 'lg',
  withSelectAll = false,
  selectAllLabel,
  alignment,
  useObjectValues,
}: CheckboxGroupInputProps<ObjectValues>) => {
  type ValueType = ObjectValues extends true ? Option[] : string[];

  // update useField to use the generic type
  const [input, meta, helpers] = useField<ValueType>(name);

  const { value: formikValue } = input;
  const { error } = meta;
  const hasError = !!error;
  const wrapperValidation = hasError ? { validation: { valid: !hasError, message: error?.toString() } } : {};
  const inputValue = Array.isArray(formikValue) ? formikValue : [];

  const onChange = (newVal: Option[] | string[]) => {
    helpers.setValue(newVal as ValueType);
  };

  return (
    <WrappedUnconnectedCheckboxGroup
      inputProps={{
        ...inputProps,
        name,
        options,
        onChange,
        value: inputValue,
        disabled,
        size,
        withSelectAll,
        selectAllLabel,
        alignment,
        useObjectValues,
      }}
      wrapperProps={{ ...wrapperProps, ...wrapperValidation, id: name, disabled, label, helpText }}
    />
  );
};
